import React, { memo } from "react";
import { View } from "react-native";
import { cn } from "@/lib/utils";

export interface ProgressBarProps {
  /** Current step (1-indexed). Segments at index < step are filled. */
  step: number;
  /** Total number of segments. */
  totalSteps: number;
  /** Extra Tailwind classes merged onto the root container. */
  className?: string;
  /** Forwarded to the root View for testing. */
  testID?: string;
}

/**
 * Segmented horizontal progress bar.
 *
 * Renders `totalSteps` equally-sized pill segments. Segments before the
 * current `step` are filled (green); remaining segments are muted.
 *
 * @example
 * ```tsx
 * <ProgressBar step={2} totalSteps={5} />
 * ```
 */
export const ProgressBar = memo(function ProgressBar({
  step,
  totalSteps,
  className,
  testID,
}: ProgressBarProps) {
  return (
    <View className={cn("flex-row gap-1", className)} testID={testID}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <View
          key={i}
          testID={`progress-bar-segment-${i}`}
          className={`flex-1 h-1 rounded-full ${i < step ? "bg-green-50" : "bg-stroke-20"}`}
        />
      ))}
    </View>
  );
});
